import { Navigate } from 'react-router-dom';
import { useMyAccounts } from './AccountContext';
import { readLastAccountId } from './last-account';
import ClaimLinePage from '../pages/ClaimLinePage';

/** What `/` does: send you to a line, never show one.
 *
 *  `/` is the manifest's `start_url`, so this is what the installed app opens. A phone
 *  app should land its owner in the work, not in a menu — which is why the picker is a
 *  place of its own at `/accounts` and this only redirects. Someone with a single line,
 *  the ordinary customer, never sees a picker at all.
 *
 *  The one case that renders instead of redirecting is having no line yet: a delegate
 *  who was invited into someone else's account and then had their access revoked, or
 *  who never had one. Redirecting them somewhere would need somewhere to redirect to.
 *
 *  Deliberately no redirect between `/` and `/accounts` in either direction — two
 *  components each able to redirect at the other is the ping-pong RoleGate exists to
 *  document. Both render the claim screen when the list is empty. */
export default function AccountResolver() {
  const { accounts, isLoading } = useMyAccounts();

  if (isLoading) return null;
  if (accounts.length === 0) return <ClaimLinePage />;

  // Reopen the last line if it is still one of yours — an owner who was helping out
  // in someone else's account yesterday should not be dropped back into their own,
  // and a line they were removed from must not be reopened at all.
  const last = readLastAccountId();
  const remembered = last && accounts.some((a) => a.accountId === last) ? last : null;
  const only = accounts.length === 1 ? accounts[0]!.accountId : null;
  const target = remembered ?? only;

  return target ? <Navigate to={`/a/${target}`} replace /> : <Navigate to="/accounts" replace />;
}
